import { useCallback, useMemo, useState } from 'react';

import type { DataNode } from 'antd/es/tree';

import { getNodeKey, getNodeTitle } from '@/shared/lib/tree';

type UseTreeSearchReturn = {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filteredTreeData: DataNode[];
  matchedParentKeys: Set<string>;
  allExpandedKeys: string[];
  isChildOfMatchedParent: (nodeKey: string) => boolean;
};

export const useTreeSearch = (treeData: DataNode[]): UseTreeSearchReturn => {
  const [searchTerm, setSearchTerm] = useState('');

  const { filteredTreeData, matchedParentKeys } = useMemo(() => {
    if (!searchTerm.trim()) {
      return { filteredTreeData: treeData, matchedParentKeys: new Set<string>() };
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    const localMatchedParentKeys = new Set<string>();

    const checkMatch = (node: DataNode): boolean => {
      const title = getNodeTitle(node);
      return title.toLowerCase().includes(lowerSearchTerm);
    };

    const filterNodes = (nodes: DataNode[]): DataNode[] => {
      const filtered: DataNode[] = [];

      for (const node of nodes) {
        const isMatched = checkMatch(node);
        const filteredChildren = node.children ? filterNodes(node.children) : [];

        if (isMatched || filteredChildren.length > 0) {
          if (isMatched && node.children && node.children.length > 0) {
            localMatchedParentKeys.add(getNodeKey(node));
          }

          filtered.push({
            ...node,
            children: isMatched ? node.children : filteredChildren.length > 0 ? filteredChildren : undefined,
          });
        }
      }

      return filtered;
    };

    const result = filterNodes(treeData);
    return { filteredTreeData: result, matchedParentKeys: localMatchedParentKeys };
  }, [treeData, searchTerm]);

  const isChildOfMatchedParent = useCallback(
    (nodeKey: string): boolean => {
      const findInTree = (nodes: DataNode[], targetKey: string, parentKey?: string): string | null => {
        for (const node of nodes) {
          if (node.key === targetKey) {
            return parentKey || null;
          }
          if (node.children) {
            const found = findInTree(node.children, targetKey, getNodeKey(node));
            if (found !== null) {
              return found;
            }
          }
        }
        return null;
      };

      const checkParentChain = (key: string): boolean => {
        const parentKey = findInTree(filteredTreeData, key);
        if (!parentKey) return false;
        if (matchedParentKeys.has(parentKey)) return true;
        return checkParentChain(parentKey);
      };

      return checkParentChain(nodeKey);
    },
    [filteredTreeData, matchedParentKeys]
  );

  const allExpandedKeys = useMemo(() => {
    const keys: string[] = [];
    const collectKeys = (nodes: DataNode[]) => {
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          keys.push(getNodeKey(node));
          collectKeys(node.children);
        }
      });
    };
    collectKeys(filteredTreeData);
    return keys;
  }, [filteredTreeData]);

  return {
    searchTerm,
    setSearchTerm,
    filteredTreeData,
    matchedParentKeys,
    allExpandedKeys,
    isChildOfMatchedParent,
  };
};
